import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Card, Row, Col, Badge, Form } from 'react-bootstrap';
import TruckFormation, { FormationPoint } from '../component/TruckFormation';

interface Props {
    show: boolean;
    onHide: () => void;
    formationPoints: FormationPoint[];
    selectedBoxIndex: number | null;
    selectedLayer: number;
    loadedItems: any[];
    onBoxClick: (point: FormationPoint, idx: number) => void;
    onLayerChange: (layer: number) => void;

    // Ürün Seçimi Props
    loadingMode: 'FREE' | 'PLAN';
    setLoadingMode: (mode: 'FREE' | 'PLAN') => void;
    products: any[];
    onSearchProducts: (items: any[] | null) => void;
    selectedProduct: any | null;
    onSelectProduct: (product: any) => void;
    plannedItems: any[];

    // Müşteri Seçimi Props (Serbest Mod İçin)
    buyers: any[];
    selectedBuyer: any | null;
    onSelectBuyer: (buyer: any) => void;

    // Yükleme Formu Props
    warehouses: any[];
    selectedWarehouse: any | null;
    onSelectWarehouse: (warehouse: any) => void;
    quantity: string;
    onQuantityChange: (val: string) => void;
    onLoadItem: () => void;

    // Silme Props
    onDeleteItem: (item: any) => void;
}

const ShipmentFormationModal: React.FC<Props> = (props) => {

    const productsToShow = props.products;
    const [searchTerm, setSearchTerm] = useState("");

    // 🔥 GÜNCELLEME: Müşteri Arama State'leri
    const [buyerSearch, setBuyerSearch] = useState("");
    const [showBuyerList, setShowBuyerList] = useState(false);
    // Dropdown dışına tıklandığında kapatmak için ref
    const buyerDropdownRef = useRef<HTMLDivElement>(null);

    // 🔥 GÜNCELLEME: Dışarıdan veya listeden müşteri seçildiğinde input'u doldur
    useEffect(() => {
        if (props.selectedBuyer) {
            setBuyerSearch(`${props.selectedBuyer.title} (${props.selectedBuyer.code})`);
        } else {
            // Eğer seçim kaldırıldıysa veya boşsa, ve kullanıcı elle silmiyorsa (focus yoksa) temizle
            if (!showBuyerList) {
                setBuyerSearch("");
            }
        }
    }, [props.selectedBuyer, showBuyerList]);

    // 🔥 GÜNCELLEME: Dışarı tıklama kontrolü
    useEffect(() => {
        function handleClickOutside(event: any) {
            if (buyerDropdownRef.current && !buyerDropdownRef.current.contains(event.target)) {
                setShowBuyerList(false);
                // Eğer geçerli bir seçim yoksa inputu temizle veya eski seçime döndür
                if (props.selectedBuyer) {
                     setBuyerSearch(`${props.selectedBuyer.title} (${props.selectedBuyer.code})`);
                } else {
                    setBuyerSearch("");
                }
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [props.selectedBuyer]);

    // Ürün Filtreleme Mantığı
    const filteredList = productsToShow.filter(p => {
        if (!searchTerm) return true;
        const term = searchTerm.toLocaleLowerCase('tr');

        const matchName = (p.productName || p.description || p.name || "").toLocaleLowerCase('tr').includes(term);
        const matchBuyer = (p.buyerName || "").toLocaleLowerCase('tr').includes(term);
        const matchOrder = (p.orderCode || "").toLocaleLowerCase('tr').includes(term);
        const matchWarehouse = (p.targetWarehouse || "").toLocaleLowerCase('tr').includes(term);

        return matchName || matchBuyer || matchOrder || matchWarehouse;
    });

    // 🔥 GÜNCELLEME: Müşteri Filtreleme Mantığı
    const filteredBuyers = props.buyers.filter(b => {
        if (!buyerSearch) return true;
        // Eğer inputtaki değer zaten seçili olanın aynısıysa hepsini göster (seçim yapılmış hali)
        if (props.selectedBuyer && buyerSearch === `${props.selectedBuyer.title} (${props.selectedBuyer.code})`) return true;

        const term = buyerSearch.toLocaleLowerCase('tr');
        return (b.title || "").toLocaleLowerCase('tr').includes(term) ||
               (b.code || "").toLocaleLowerCase('tr').includes(term);
    });

    useEffect(() => {
        setSearchTerm("");
    }, [props.loadingMode]);

    const isAddButtonDisabled =
        !props.selectedProduct ||
        !props.quantity ||
        !props.selectedWarehouse ||
        (props.loadingMode === 'FREE' && !props.selectedBuyer);

    return (
        <Modal
            show={props.show}
            onHide={props.onHide}
            size="xl"
            centered
            dialogClassName="modal-90w"
            backdrop="static"
        >
            <Modal.Header closeButton className="py-2">
                <Modal.Title className="fs-5">Sevkiyat Formasyon & Ürün Yükleme</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ minHeight: '600px', backgroundColor: '#f8f9fa' }}>
                <Row className="h-100">
                    {/* ----------------------------------------------------------- */}
                    {/* 1. KOLON: FORMASYON (SOL) */}
                    {/* ----------------------------------------------------------- */}
                    <Col lg={5} md={12} className="mb-3">
                        <Card className="h-100 shadow-sm border-0">
                            <Card.Header className="bg-primary text-white fw-bold">
                                Palet Formasyonu
                            </Card.Header>
                            <Card.Body style={{ overflow: 'auto', position: 'relative' }}>
                                <div className="mb-3 d-flex align-items-center">
                                    <span className="fw-bold me-2">Kat:</span>
                                    {Array.from(new Set(props.formationPoints.map(p => p.layer)))
                                        .sort((a, b) => a - b)
                                        .map(layer => (
                                            <Button
                                                key={layer}
                                                size="sm"
                                                variant={props.selectedLayer === layer ? 'primary' : 'outline-secondary'}
                                                onClick={() => props.onLayerChange(layer)}
                                                className="me-1"
                                                style={{ minWidth: '40px' }}
                                            >
                                                {layer}
                                            </Button>
                                        ))
                                    }
                                </div>

                                <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px', backgroundColor: '#fff', minHeight: '400px' }}>
                                    <TruckFormation
                                        formationPoints={props.formationPoints}
                                        selectedBoxIndex={props.selectedBoxIndex ?? undefined}
                                        onBoxClick={props.onBoxClick}
                                        layer={props.selectedLayer}
                                        loadedItems={props.loadedItems}
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* ----------------------------------------------------------- */}
                    {/* 2. KOLON: ÜRÜN & MÜŞTERİ SEÇİMİ (ORTA) */}
                    {/* ----------------------------------------------------------- */}
                    <Col lg={4} md={6} className="mb-3">
                        <Card className="h-100 shadow-sm border-0">
                            <Card.Header className="bg-success text-white fw-bold">
                                Seçili Palete Ürün Ekle
                            </Card.Header>
                            <Card.Body className="d-flex flex-column">

                                {/* Mod Seçimi */}
                                <div className="bg-light p-2 rounded border mb-3 text-center d-flex justify-content-center gap-3">
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input" type="radio" id="modePlan"
                                            checked={props.loadingMode === 'PLAN'}
                                            onChange={() => props.setLoadingMode('PLAN')}
                                            style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                        />
                                        <label className="form-check-label fw-bold ms-1" htmlFor="modePlan" style={{ cursor: 'pointer' }}>Plana Göre</label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input" type="radio" id="modeFree"
                                            checked={props.loadingMode === 'FREE'}
                                            onChange={() => props.setLoadingMode('FREE')}
                                            style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                        />
                                        <label className="form-check-label fw-bold ms-1" htmlFor="modeFree" style={{ cursor: 'pointer' }}>Serbest</label>
                                    </div>
                                </div>

                                {/* 🔥 GÜNCELLEME: Arama Yapılabilir Müşteri Seçimi (Custom Dropdown) */}
                                {props.loadingMode === 'FREE' && (
                                    <div className="mb-3 position-relative" ref={buyerDropdownRef}>
                                        <label className="small fw-bold text-muted mb-1">Müşteri (Cari) Seçimi</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white border-end-0">
                                                🔍
                                            </span>
                                            <Form.Control
                                                type="text"
                                                placeholder="Müşteri Ara..."
                                                className="border-start-0 border-warning shadow-sm"
                                                value={buyerSearch}
                                                onChange={(e) => {
                                                    setBuyerSearch(e.target.value);
                                                    setShowBuyerList(true);
                                                    if(e.target.value === "") props.onSelectBuyer(null); // Silince seçimi kaldır
                                                }}
                                                onFocus={() => setShowBuyerList(true)}
                                                autoComplete="off"
                                            />
                                            {props.selectedBuyer && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => {
                                                        props.onSelectBuyer(null);
                                                        setBuyerSearch("");
                                                    }}
                                                    title="Seçimi Temizle"
                                                >
                                                    ✕
                                                </Button>
                                            )}
                                        </div>

                                        {/* Dropdown Listesi */}
                                        {showBuyerList && (
                                            <div
                                                className="position-absolute w-100 bg-white border rounded shadow-lg"
                                                style={{
                                                    zIndex: 1050,
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    top: '100%'
                                                }}
                                            >
                                                {filteredBuyers.length === 0 ? (
                                                    <div className="p-2 text-muted text-center small">Sonuç bulunamadı</div>
                                                ) : (
                                                    filteredBuyers.map((b: any) => (
                                                        <div
                                                            key={b.id}
                                                            className="p-2 border-bottom cursor-pointer hover-bg-light"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                props.onSelectBuyer(b);
                                                                setBuyerSearch(`${b.title} (${b.code})`);
                                                                setShowBuyerList(false);
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                        >
                                                            <div className="fw-bold small text-dark">{b.title}</div>
                                                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{b.code}</div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Arama Inputu */}
                                <div className="mb-2">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={props.loadingMode === 'PLAN' ? "🔍 Planda ara (Ürün, Depo, Sipariş...)" : "🔍 Tüm ürünlerde ara..."}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>

                                {/* Ürün Listesi */}
                                <div className="flex-grow-1 mt-2 border rounded p-0 bg-white"
                                     style={{
                                         maxHeight: '300px',
                                         overflowY: 'auto',
                                         WebkitOverflowScrolling: 'touch',
                                         touchAction: 'pan-y'
                                     }}>

                                    {filteredList.length === 0 && (
                                        <div className="text-muted small text-center mt-4">
                                            {props.loadingMode === 'PLAN' ? 'Planda eşleşen ürün bulunamadı.' : 'Ürün bulunamadı.'}
                                        </div>
                                    )}

                                    {filteredList.slice(0, 100).map(p => {
                                        const isSelected = props.selectedProduct?.id === p.id;
                                        const displayName = p.productName || p.description || p.name || "İsimsiz Ürün";

                                        return (
                                            <div
                                                key={p.id}
                                                onClick={() => props.onSelectProduct(p)}
                                                className={`d-flex align-items-center p-3 border-bottom transition-all ${
                                                    isSelected ? 'bg-success-subtle border-start border-5 border-success' : 'hover-bg-light'
                                                }`}
                                                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                            >
                                                <div className="me-3">
                                                    <input
                                                        className="form-check-input" type="radio"
                                                        checked={isSelected} readOnly
                                                        style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                                                    />
                                                </div>

                                                <div className="flex-grow-1">
                                                    <div className={`fw-bold mb-1 ${isSelected ? 'text-success-emphasis' : 'text-dark'}`} style={{ fontSize: '0.95rem' }}>
                                                        {displayName}
                                                    </div>

                                                    {p.isPlanItem ? (
                                                        <>
                                                            <div className="d-flex align-items-center flex-wrap gap-2 text-muted small mb-1">
                                                                <span title="Müşteri">👤 {p.buyerName}</span>
                                                                <span className="text-secondary opacity-50">|</span>
                                                                <span title="Sipariş No" className="font-monospace bg-light border px-1 rounded">#{p.orderCode}</span>
                                                            </div>
                                                            <div className="small fw-bold text-primary">
                                                                📍 {p.targetWarehouse || "Depo Yok"}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="small text-muted">{p.code || ""}</div>
                                                    )}
                                                </div>

                                                {p.isPlanItem && (
                                                    <div className="ms-2 text-end">
                                                        <Badge
                                                            bg="warning" text="dark"
                                                            className="shadow-sm border border-dark border-opacity-25 py-2 px-3"
                                                            style={{ fontSize: '0.9rem' }}
                                                        >
                                                            PLAN: {p.plannedQuantity}
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Alt Form */}
                                <div className="mt-3 border-top pt-3">
                                    <select
                                        className="form-control mb-2"
                                        value={props.selectedWarehouse?.id || ""}
                                        onChange={(e) => {
                                            const w = props.warehouses.find(wh => wh.id === Number(e.target.value));
                                            if(w) props.onSelectWarehouse(w);
                                        }}
                                        disabled={!props.selectedProduct}
                                    >
                                        <option value="">Depo Seçin</option>
                                        {props.warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                    </select>

                                    <div className="d-flex gap-2">
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Adet"
                                            value={props.quantity}
                                            onChange={e => props.onQuantityChange(e.target.value)}
                                            disabled={!props.selectedProduct}
                                        />
                                        <Button
                                            variant="success"
                                            onClick={props.onLoadItem}
                                            disabled={isAddButtonDisabled}
                                            className="px-4 fw-bold"
                                        >
                                            EKLE
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* ----------------------------------------------------------- */}
                    {/* 3. KOLON: MEVCUT ÜRÜNLER (SAĞ) */}
                    {/* ----------------------------------------------------------- */}
                    <Col lg={3} md={6} className="mb-3">
                        <Card className="h-100 shadow-sm border-0">
                            <Card.Header className="bg-white border-bottom fw-bold text-dark py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>Paletteki Ürünler</span>
                                    {props.selectedBoxIndex !== null && <Badge bg="warning" text="dark">#{props.selectedBoxIndex}</Badge>}
                                </div>
                            </Card.Header>
                            <Card.Body style={{ overflowY: 'auto', maxHeight: '600px', backgroundColor: '#fdfdfd', WebkitOverflowScrolling: 'touch' }}>
                                {props.selectedBoxIndex === null ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center h-50 text-muted">
                                        <div className="fs-1 mb-2">👈</div>
                                        <div>Lütfen soldan bir palet seçin.</div>
                                    </div>
                                ) : (
                                    <>
                                        {props.loadedItems.filter(i => i.palletIndex === props.selectedBoxIndex).length === 0 && (
                                            <div className="text-center text-muted py-5">
                                                <div className="fs-5 opacity-50">📦</div>
                                                <small>Bu palet henüz boş.</small>
                                            </div>
                                        )}
                                        {props.loadedItems
                                            .filter(item => item.palletIndex === props.selectedBoxIndex)
                                            .map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    className="mb-3 bg-white shadow-sm rounded border-0 position-relative overflow-hidden"
                                                    style={{ borderLeft: '4px solid #ffc107' }}
                                                >
                                                    <div className="p-3">
                                                        {/* Ürün Adı */}
                                                        <div className="fw-bold text-dark mb-2" style={{ fontSize: '0.95rem', lineHeight: '1.4' }}>
                                                            {item.productName}
                                                        </div>

                                                        {/* Müşteri Adı ve Altında Hedef Depo */}
                                                        <div className="mb-2">
                                                            <div className="d-flex justify-content-between align-items-start">
                                                                {/* Sol: Müşteri ve Altında Hedef */}
                                                                <div className="d-flex flex-column">
                                                                    <span className="text-primary fw-semibold small" style={{ fontSize: '0.85rem' }}>
                                                                        {item.buyerTitle || 'Serbest Cari'}
                                                                    </span>
                                                                    {item.targetWarehouse && (
                                                                        <span className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                                                                            📍 {item.targetWarehouse}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {/* Sağ: Sipariş No */}
                                                                <span className="badge bg-light text-secondary border fw-normal ms-2" style={{ fontSize: '0.7rem' }}>
                                                                    {item.orderNumber || 'SRB'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <hr className="my-2 border-light" />

                                                        {/* Alt Kısım: Kaynak Depo ve Miktar */}
                                                        <div className="d-flex justify-content-between align-items-end">
                                                            <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                                                <span className="text-warning me-1">📥</span>
                                                                {item.warehouseName}
                                                            </div>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="text-end" style={{ lineHeight: '1' }}>
                                                                    <span className="fs-4 fw-bold text-dark">{item.quantity}</span>
                                                                    <span className="small text-muted ms-1" style={{ fontSize: '0.7rem' }}>Adet</span>
                                                                </div>
                                                                <button
                                                                    className="btn btn-link text-danger p-0 ms-2"
                                                                    style={{ textDecoration: 'none', opacity: 0.7 }}
                                                                    onClick={() => props.onDeleteItem(item)}
                                                                >
                                                                    <span className="fs-5">×</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    );
};

export default ShipmentFormationModal;